/**
 * axios 实例封装
 * 2022-06-23
 */

/**
 * $axios 与原生 axios 的用法相同
 *
 * config = { //额外支持的参数配置
 *    cancelFun: (cancelFunction) => {}, //取消回调函数，将中止请求的方法以参数方式传递
 *    isReturnResponse: false, //如果存在，直接返回 response 不进行错误码校验，优先级最高
 *    isResFull: false, //如果存在响应完整的返回包，否则返回 res.data ，默认否，优先级次之
 *    noHandle401: false, //如果存在，不处理 401 错误(会话失效问题)，默认否处理401，请先挂载 $axios.handle401(msg, res.body) 方法
 *    errorPop: false, //如果存在，接口报错执行，弹出toast，如果为函数将调用 (errMsg，res.body) => {}
 *    successPop: (res.body) => {}, //如果存在，接口报错执行，弹出toast，如果为函数将调用 (msg, res.body) => {}
 * }
 *
 * catch(e) { //可被捕获的错误类型
 *  if (typeof e.code === 'number') { //接口报错
 *    //e = {code: 302, info: '报错信息', data: {}}
 *    //e = {code: 302, msg: '报错信息', data: {}}
 *  } else if (e.isCancel) { //请求取消
 *    //e= {isCancel: true, message: '可选信息'}
 *  } else if (e.isError) { //通信失败
 *    //e= {isError: true, message: '未联网'}
 *  }
 * }
 */

import axios from "axios";
import { message } from "antd";

const getApiType = (resData) => {
  if (resData.rc !== undefined) return "xt4";
  if (resData.code !== undefined && (resData.info !== undefined || resData.msg !== undefined)) return "xt3";
  if (resData.rc !== undefined && resData.code !== undefined) return "finance"; //理财
  if (resData.returnCode !== undefined) return "futures";
}

const getTransRes = (intl, key, params) => {
  if (!intl) return key || "";
  if (!key) return "";
  return intl.formatMessage({ id: key }, params);
}

/**
 * @description 统一三种接口模式下的数据格式
 * @param {Object} resData 后台返回的data数据 response.data
 * @param {Function} t 翻译函数
 * @param {Function} te 判断key是否存在
 */
const getFormatResData = (resData, intl) => {
  const apiType = getApiType(resData);
  const formatResData = {
    code: "",
    msg: "",
    data: null,
    errCode: ""
  };
  if (apiType === "xt4") {
    formatResData.msg = getTransRes(intl, resData.mc, resData.ma) || resData.mc;
    formatResData.code = resData.rc === 0 ? 200 : resData.rc;
    formatResData.data = resData.result;
    formatResData.errCode = resData.mc;
  } else if (apiType === "finance") {
    formatResData.msg = getTransRes(intl, resData.mc, resData.ma) || resData.msg;
    formatResData.code = resData.code == "0" ? 200 : resData.code;
    formatResData.data = resData.data;
    formatResData.errCode = resData.mc;
  } else if (apiType === "futures") {
    formatResData.code = resData.returnCode === 0 ? 200 : resData.returnCode;
    formatResData.msg =
      resData.returnCode === 0
        ? getTransRes(intl, resData.msgInfo) || resData.msgInfo
        : getTransRes(intl, resData.error.code) || resData.error.msg || resData.msgInfo;
    formatResData.data = resData.result;
    if (resData.returnCode != 0 && resData.error) {
      if (!formatResData.data)
        formatResData.data = {};
      formatResData.data.error = resData.error;
    }
    formatResData.errCode = resData.error?.code;
  } else if (apiType === "xt3") {
    const defaultMsg = resData.msg || resData.info;
    formatResData.code = resData.code;
    formatResData.msg = !resData.msgInfo
      ? defaultMsg
      : getTransRes(intl, resData.msgInfo.template, resData.msgInfo.args) || defaultMsg;
    formatResData.data = resData.data;
    formatResData.errCode = resData.msgInfo?.template;
  } else {
    return { ...resData };
  }
  return formatResData;
}

const CancelToken = axios.CancelToken;
const axiosInstance = axios.create({
  timeout: 10000,
  // withCredentials: true,
  headers: {
    device: "web",
    lang: "en",
    "api-version": "4",
  },
});

const $axios = (config) => {
  const options = {
    ...(config || {}),
  };
  if (config.cancelFun && typeof config.cancelFun === "function") {
    options.cancelToken = new CancelToken(config.cancelFun);
  }

  return new Promise((resolve, reject) => {
    axiosInstance(options)
      .then((response) => {

        if (options.isReturnResponse) return resolve(response);

        const intl = axiosInstance.intl; // 获取 intl 对象
        const resData = response.data; //返回数据
        const formatResData = getFormatResData(resData, intl);

        if (formatResData.code === 200) {
          if (options.successPop) {
            if (typeof options.successPop === "function") {
              options.successPop(formatResData.msg, resData);
            } else {
              message.success(formatResData.msg);
            }
          }

          return resolve(options.isResFull ? resData : resData.data);
        }

        resData.message = formatResData.msg;
        //会话失效
        if (/^(302|401)$/.test(resData.code) && !options.noHandle401) {
          console.error("【logErr】", resData.message, options.url);
          if (typeof $axios.handle401 === "function") {
            $axios.handle401(resData.message, resData);
            return reject(resData);
          }
        }

        if (options.errorPop) {
          if (typeof options.errorPop === "function") {
            options.errorPop(resData.message, resData);
          } else {
            message.error(resData.message);
          }
        }

        return reject(resData);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          error.isCancel = true; //请求取消
          console.log("%c【axios cancel】%o", "color:#eb2f96", error);
        } else {
          error.isError = true; //通信失败
          console.log("%c【axios error】%o", "color:#eb2f96", error);
        }

        if (options.errorPop && error.message) {
          if (typeof options.errorPop === "function") {
            options.errorPop(error.message, error);
          } else {
            message.error(error.message);
          }
          console.error("【logErr pop】", error.message, options.url);
        } else {
          console.error("【logErr】", error.message, options.url);
        }

        return reject(error);
      })
      .finally(() => {
        // always executed
      });
  });
};
["get", "delete", "head", "options"].map((method) => {
  $axios[method] = (url, config) => {
    return $axios({
      method,
      url,
      ...(config || {}),
    });
  };
});
["post", "put", "patch"].map((method) => {
  $axios[method] = (url, data, config) => {
    return $axios({
      method,
      url,
      data,
      ...(config || {}),
    });
  };
});
$axios.instance = axiosInstance;
$axios.asyncAwait = async (func, ...rest) => {
  try {
    let data = await func(...rest);
    return { data };
  } catch (e) {
    return { err: e.message, data: e };
  }
};

$axios.install = (app, options) => {
  if (!options) return;
  const { headers, alertFun, invalidFun } = options;
  if (headers && typeof headers === "object") {
    axiosInstance.defaults.headers = {
      ...axiosInstance.defaults.headers,
      ...headers,
    };
  }
  if (alertFun && typeof alertFun === "function") {
    ApiFun.alert = alertFun;
  }
  if (invalidFun && typeof invalidFun === "function") {
    ApiFun.invalid = invalidFun;
  }
};

export default $axios;
